import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { RuntimeConfig } from './runtime-config';

const CONFIG_URL = '/runtime-config.json';

const DEFAULT_CONFIG: RuntimeConfig = {
  production: false,
  apiBase: 'http://192.168.0.127:8000/',
  frontendUrl: ''
};

const HOST_OVERRIDES: Record<string, Partial<RuntimeConfig>> = {
  
  'dev-career.botshreyasi.com': {
    production: false,
    apiBase: 'https://dev-api.botshreyasi.com/',
    frontendUrl: 'https://dev-career.botshreyasi.com'
  },
  'career.botshreyasi.com': {
    production: true,
    apiBase: 'https://api.botshreyasi.com/',
    frontendUrl: 'https://career.botshreyasi.com'
  },
  'localhost': {
    production: false,
    apiBase: 'http://0.0.0.0:8000/',
    frontendUrl: 'http://localhost:4200'
  }
};

@Injectable({ providedIn: 'root' })
export class RuntimeConfigLoader {
  private config?: RuntimeConfig;

  constructor(private readonly http: HttpClient) {}

  load(): Promise<void> {
    
    return lastValueFrom(
      this.http.get<RuntimeConfig>(CONFIG_URL).pipe(
        map((config) => {
          return this.applyOverrides({ ...DEFAULT_CONFIG, ...config })
        }),
        tap((config) => {
          this.config = config;
        }),
        catchError(() => {
          
          const fallback = this.applyOverrides(DEFAULT_CONFIG);
          this.config = fallback;
          return of(fallback);
        })
      )
    ).then(() => undefined);
  }

  get(): RuntimeConfig {
    if (!this.config) {
      throw new Error('Runtime configuration not loaded yet');
    }
    return this.config;
  }

  private applyOverrides(config: RuntimeConfig): RuntimeConfig {
    const hostname = this.getHostname();
    
    const override = hostname ? HOST_OVERRIDES[hostname] : undefined;
    const frontendUrl = override?.frontendUrl ?? config.frontendUrl ?? this.getOrigin();
    return {
      ...config,
      ...override,
      frontendUrl
    };
  }

  private getHostname(): string {
    return typeof window === 'undefined' ? '' : window.location.hostname;
  }

  private getOrigin(): string {
    return typeof window === 'undefined' ? '' : window.location.origin;
  }
}
